import React from 'react';
import {debounce} from 'ts-debounce';

export const DEBOUNCE_MS = 250;

interface WrappedComponentProps {
  onChangeValue: (value: WrappedComponentProps['value']) => void;
  value: string | (number | null);
}
interface WithDebouncingProps extends WrappedComponentProps {
  initialValue: WrappedComponentProps['value'];
}
interface State {
  value: WrappedComponentProps['value'];
}

/**
 * HOC that debounces all changes to the wrapped component
 *
 * Note that the resulting component is uncontrolled -- the `initialValue` prop is not synced.
 */
export default function withDebouncing<P>(
  WrappedComponent: React.ComponentType<WrappedComponentProps & P>
): React.ComponentClass<Omit<WithDebouncingProps & P, 'value'>, State> {
  class WithDebouncing extends React.Component<Omit<WithDebouncingProps & P, 'value'>, State> {
    constructor(props: Omit<WithDebouncingProps & P, 'value'>) {
      super(props);
      this.state = {value: props.initialValue};
    }

    componentDidUpdate(prevProps: Omit<WithDebouncingProps & P, 'value'>): void {
      if (this.props.initialValue !== prevProps.initialValue) {
        this.setState({value: this.props.initialValue});
      }
    }

    _handleParentChange = (value: string): void => {
      this.props.onChangeValue(value);
    };

    handleChangeDebounced = debounce(this._handleParentChange, DEBOUNCE_MS);

    handleChange = (value: string): void => {
      this.setState({value});
      this.handleChangeDebounced(value);
    };

    render(): JSX.Element {
      return (
        <WrappedComponent
          // TS isn't smart enough to figure out that excluding and re-adding value works, so just
          // do an unsafe cast to P to work around this.
          {...(this.props as P)}
          onChangeValue={this.handleChange}
          value={this.state.value}
        />
      );
    }
  }
  return WithDebouncing;
}
